/**
 * PPLA (Argox Printer Language A / Datamax DPL uyumlu) komut üreticisi.
 *
 * Bitmap basımı iki adımlı: önce görüntü yazıcı belleğine indirilir
 * (<STX>IAb), sonra etiket formatı içinde konumlandırılır (1Y satırı).
 *
 * Argox OS-214 plus üzerinde canlı doğrulanmış davranışlar:
 *   - <SOH>D olmadan grafik verisi komut olarak yorumlanıp bozuluyor
 *   - <STX>m metrik mod şart, birim 0.1 mm
 *   - Yazıcı BMP paletini yok sayar: bit 0 = mürekkep (bmp1bit.ts'de ele alınıyor)
 */

import { ascii, concat, pad } from './byteUtils';

const SOH = '\x01';
const STX = '\x02';
const CR = '\r';

/** Yazıcı belleğindeki grafik adı — 8 karakter sınırı var */
const DEFAULT_GRAPHIC_NAME = 'GTSLBL';

export interface PplaJobOptions {
  /** Etiketin sol kenarından uzaklık (mm) */
  offsetXMm?: number;
  /** Etiketin üst kenarından uzaklık (mm) */
  offsetYMm?: number;
  /** Basılacak kopya sayısı (1-9999) */
  copies?: number;
  /** Isı/koyuluk 0-20. Verilmezse yazıcının kendi ayarı kullanılır. */
  heat?: number;
  /** Baskı hızı harfi (A-K). Verilmezse yazıcı ayarı kullanılır. */
  speed?: string;
  /** Bellekteki grafik adı */
  graphicName?: string;
}

/** Milimetreyi PPLA metrik birimine (0.1 mm) çevirir */
function mmToUnits(mm: number): number {
  return Math.round(mm * 10);
}

/**
 * Tek bir bitmap etiketi için tam PPLA iş akışını üretir.
 * `bmpBytes` 1-bit monokrom BMP olmalı (bkz. bmp1bit.ts).
 */
export function buildPplaBitmapJob(bmpBytes: Uint8Array, options?: PplaJobOptions): Uint8Array {
  const name = (options?.graphicName ?? DEFAULT_GRAPHIC_NAME).slice(0, 8);
  const copies = Math.min(9999, Math.max(1, Math.round(options?.copies ?? 1)));
  const row = mmToUnits(options?.offsetYMm ?? 0);
  const column = mmToUnits(options?.offsetXMm ?? 0);

  const chunks: Uint8Array[] = [];
  const push = (s: string) => chunks.push(ascii(s));

  // Interaction komutlarını kapat — ham grafik verisi komut sanılmasın
  push(`${SOH}D${CR}`);
  push(`${STX}m${CR}`);

  if (options?.heat !== undefined) {
    push(`${STX}H${pad(Math.min(20, options.heat), 2)}${CR}`);
  }
  if (options?.speed) {
    push(`${STX}PS${options.speed}${CR}`);
  }

  // Grafiği belleğe indir
  push(`${STX}IAb${name}${CR}`);
  chunks.push(bmpBytes);
  push(CR);

  // Etiket formatı
  push(`${STX}L${CR}`);
  push(`D11${CR}`);
  if (copies > 1) push(`Q${pad(copies, 4)}${CR}`);
  // 1=döndürme yok, Y=bellekteki grafik, 11=en/boy çarpanı, 000=ayrılmış
  // Ardından 4 hane satır (Y), 4 hane sütun (X) — DPL konum sırası
  push(`1Y11000${pad(row, 4)}${pad(column, 4)}${name}${CR}`);
  push(`E${CR}`);

  // Belleği temizle — sonraki işte isim çakışması olmasın
  push(`${STX}Q${CR}`);

  return concat(chunks);
}

/**
 * Aynı grafiği tek seferde yükleyip birden fazla etiket formatı basar.
 * Farklı kopya sayıları veya konumlar gerektiğinde kullanılır.
 */
export function buildPplaMultiJob(
  jobs: Array<{ bmpBytes: Uint8Array; options?: PplaJobOptions }>,
): Uint8Array {
  const parts = jobs.map((job, index) =>
    buildPplaBitmapJob(job.bmpBytes, {
      ...job.options,
      // Her iş kendi grafiğini kullansın; isim çakışması baskıyı bozar
      graphicName: job.options?.graphicName ?? `GTS${pad(index % 100000, 5)}`,
    }),
  );
  return concat(parts);
}

/** Yazıcıyı bilinen bir duruma getirir — sıkışma sonrası kurtarma için */
export function buildPplaReset(): Uint8Array {
  return ascii(`${SOH}#${CR}`);
}
